import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDays, Zap, Users, ShieldCheck, User, Mail, Phone, Building2,
  Calendar, CheckCircle2, Send, Headphones, ChevronRight,
} from 'lucide-react';

import { API_BASE_URL } from '../apiConfig';

const CONTAINER = 'w-full max-w-7xl xl:max-w-[95%] 2xl:max-w-[85vw] mx-auto px-6 sm:px-8 lg:px-8';

/* Change this to your backend URL if the API is on a different origin. */
const API_URL = `${API_BASE_URL}/request-demo`;

const FEATURES = [
  { Icon: CalendarDays, color: '#3284c7', title: 'Personalized Demo', desc: 'Tailored to your business needs' },
  { Icon: Zap, color: '#16a34a', title: 'Quick & Easy', desc: 'Schedule at your convenience' },
  { Icon: Users, color: '#9333ea', title: 'Expert Consultation', desc: 'Talk to our solution specialists' },
  { Icon: ShieldCheck, color: '#f29132', title: 'No Obligation', desc: '100% free and commitment-free' },
];

const GET_ITEMS = [
  'Overview of SmartEdz solutions',
  'Live walkthrough of key features',
  'How it fits your business needs',
  'Implementation approach',
  'Pricing & deployment options',
  'Q&A with our experts',
];

const INDUSTRIES = ['Education', 'Healthcare', 'Manufacturing', 'Retail', 'Finance', 'Logistics', 'Hospitality', 'Construction', 'Other'];
const SOLUTIONS = ['ERP', 'CRM', 'HRMS', 'Inventory & Supply Chain', 'Finance & Accounting', 'LMS', 'Custom Software', 'Other'];
const EMPLOYEES = ['1 - 10', '11 - 50', '51 - 200', '201 - 500', '500+'];

const FIELD = 'w-full h-11 rounded-lg ring-1 ring-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-primary transition';

const RequestDemo = () => {
  const [form, setForm] = useState({
    fullName: '', workEmail: '', phone: '', company: '',
    industry: '', interestedSolution: '', employees: '', preferredDate: '',
    requirements: '', agree: false,
  });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    if (!form.fullName || !form.workEmail || !form.industry || !form.interestedSolution) {
      setStatus('error'); setMessage('Please fill in all required fields (*).'); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.workEmail)) {
      setStatus('error'); setMessage('Please enter a valid work email.'); return;
    }
    if (!form.agree) {
      setStatus('error'); setMessage('Please accept the Privacy Policy and Terms & Conditions.'); return;
    }
    try {
      setStatus('loading');
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || 'Request failed');
      setStatus('success');
      setMessage(data.message || 'Demo request received. Our team will contact you soon.');
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="bg-gradient-to-b from-[#eef4fb] via-[#f5f9fd] to-white min-h-screen overflow-x-hidden">
      <div className={`${CONTAINER} py-8 lg:py-10`}>
        <nav className="flex items-center gap-2 text-sm text-slate-500 mb-6">
          <Link to="/" className="text-primary hover:underline">Home</Link>
          <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-slate-600 font-medium">Request Demo</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* ========== LEFT ========== */}
          <div>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1]" style={{ color: '#13294b' }}>
              Request a Demo<br />
              See <span className="text-primary">SmartEdz</span> in <span style={{ color: '#f29132' }}>Action</span>
            </h1>
            <p className="mt-4 text-slate-500 max-w-lg leading-relaxed">
              Explore our innovative software solutions in a personalized demo and discover how SmartEdz can help your business automate, optimize, and grow.
            </p>

            {/* feature icons */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
              {FEATURES.map((f) => (
                <div key={f.title} className="text-center">
                  <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-2" style={{ backgroundColor: `${f.color}1A` }}>
                    <f.Icon className="w-6 h-6" style={{ color: f.color }} />
                  </div>
                  <div className="font-bold text-slate-800 text-sm">{f.title}</div>
                  <div className="text-[11px] text-slate-500 leading-tight mt-0.5">{f.desc}</div>
                </div>
              ))}
            </div>

            {/* what you'll get */}
            <div className="mt-8 rounded-2xl bg-white ring-1 ring-slate-100 shadow-sm p-5 grid sm:grid-cols-2 gap-5 items-center">
              <img
                src="/Demo_Img.png"
                alt="SmartEdz demo preview"
                className="w-full h-auto select-none"
                draggable="false"
                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
              />
              <div>
                <h3 className="font-bold text-primary mb-3">What You'll Get in the Demo</h3>
                <ul className="space-y-2.5">
                  {GET_ITEMS.map((g) => (
                    <li key={g} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      {g}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* ========== RIGHT (FORM) ========== */}
          <div className="rounded-2xl bg-white ring-1 ring-slate-200 shadow-lg p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <CalendarDays className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-extrabold" style={{ color: '#13294b' }}>Fill in Your Details</h2>
                <p className="text-sm text-slate-500">Our team will contact you to schedule a demo.</p>
              </div>
            </div>

            {status === 'success' ? (
              <div className="rounded-xl bg-green-50 ring-1 ring-green-200 p-6 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-3">
                  <CheckCircle2 className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="font-bold text-slate-800 text-lg mb-1">Thank you!</h3>
                <p className="text-slate-600 text-sm">{message}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <User className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input value={form.fullName} onChange={set('fullName')} placeholder="Enter your full name" className={`${FIELD} pl-9`} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Email <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input type="email" value={form.workEmail} onChange={set('workEmail')} placeholder="Enter your work email" className={`${FIELD} pl-9`} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone Number <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input value={form.phone} onChange={set('phone')} placeholder="Enter your phone number" className={`${FIELD} pl-9`} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Company/Organization Name</label>
                    <div className="relative">
                      <Building2 className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input value={form.company} onChange={set('company')} placeholder="Enter your company name" className={`${FIELD} pl-9`} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Industry <span className="text-red-500">*</span></label>
                    <select value={form.industry} onChange={set('industry')} className={`${FIELD} appearance-none`}>
                      <option value="">Select your industry</option>
                      {INDUSTRIES.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Interested Solution <span className="text-red-500">*</span></label>
                    <select value={form.interestedSolution} onChange={set('interestedSolution')} className={`${FIELD} appearance-none`}>
                      <option value="">Select solution</option>
                      {SOLUTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Number of Employees</label>
                    <select value={form.employees} onChange={set('employees')} className={`${FIELD} appearance-none`}>
                      <option value="">Select number of employees</option>
                      {EMPLOYEES.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Preferred Demo Date</label>
                    <div className="relative">
                      <input type="date" value={form.preferredDate} onChange={set('preferredDate')} className={`${FIELD} pr-9`} />
                      <Calendar className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Tell us about your requirements</label>
                  <textarea value={form.requirements} onChange={set('requirements')} rows={3} placeholder="Briefly describe your business needs..."
                    className="w-full rounded-lg ring-1 ring-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-primary transition resize-y" />
                </div>

                <label className="flex items-start gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={form.agree} onChange={set('agree')} className="mt-0.5 w-4 h-4 accent-[#3284c7]" />
                  <span>I agree to the <Link to="/" className="text-primary hover:underline">Privacy Policy</Link> and <Link to="/" className="text-primary hover:underline">Terms &amp; Conditions</Link>.</span>
                </label>

                {status === 'error' && message && (
                  <div className="rounded-lg bg-red-50 ring-1 ring-red-200 text-red-600 text-sm px-4 py-2.5">{message}</div>
                )}

                <button type="submit" disabled={status === 'loading'}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3.5 font-semibold text-white bg-primary shadow-lg shadow-primary/30 hover:brightness-110 transition disabled:opacity-60">
                  {status === 'loading' ? 'Submitting...' : 'Request Demo'}
                  <Send className="w-4 h-4" />
                </button>

                <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
                  <ShieldCheck className="w-3.5 h-3.5" /> Your information is safe with us. We respect your privacy.
                </p>
              </form>
            )}
          </div>
        </div>

        {/* ========== BOTTOM BAR ========== */}
        <div className="mt-8 rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm px-6 py-5 flex flex-col lg:flex-row items-center gap-6 lg:divide-x divide-slate-100">
          <div className="flex items-center gap-4 lg:pr-8">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Headphones className="w-6 h-6 text-primary" />
            </div>
            <div>
              <div className="font-bold text-slate-800">Prefer to talk to our expert?</div>
              <div className="text-sm text-slate-500">We're here to help you find the right solution.</div>
            </div>
          </div>
          <a href="tel:+916399191691" className="flex items-center gap-2 text-slate-700 font-medium lg:px-8 hover:text-primary">
            <Phone className="w-5 h-5 text-primary" /> +91 63991 91691
          </a>
          <a href="mailto:info@smartedz.com" className="flex items-center gap-2 text-slate-700 font-medium lg:px-8 hover:text-primary">
            <Mail className="w-5 h-5 text-primary" /> info@smartedz.com
          </a>
          <Link to="/contact-us" className="flex items-center gap-2 text-slate-700 font-medium lg:px-8 hover:text-primary">
            <CalendarDays className="w-5 h-5 text-primary" /> Schedule a Call
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RequestDemo;